import { forwardRef, isValidElement, cloneElement } from "react";
import Button, { BUTTON_VARIANTS, BUTTON_ICON_ALIGNMENTS } from "app/pages/.shared/form/Button";
import { useFilterContext } from "../contexts/FilterProvider";
import classNames from "classnames";
import PropTypes from "prop-types";
import FilterIcon from "app/features/product/productFilters/icons/FilterIcon";
import { FormattedMessage } from "react-intl";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import useOnSticky from "app/pages/.shared/useOnSticky";
import "./FilterTrigger.scss";

const composeHandlers = (userHandler, ourHandler) => {
	return function handleEvent(e) {
		if (typeof userHandler === "function") {
			userHandler(e);
		}

		if (e?.defaultPrevented) {
			return;
		}

		if (typeof e.isPropagationStopped === "function" && e.isPropagationStopped()) {
			return;
		}

		if (typeof ourHandler === "function") {
			ourHandler(e);
		}
	};
};

export const FilterTrigger = forwardRef(function FilterTrigger(
	{ asChild = false, className, children, onClick, ...props },
	ref
) {
	const { openDrawer, isOpen, selectedFiltersCount } = useFilterContext();

	const filterTriggerClassName = classNames("filter-trigger", className);

	const handleClick = composeHandlers(onClick, openDrawer);

	const aria = { "aria-haspopup": "dialog", "aria-expanded": isOpen };

	if (asChild) {
		if (!isValidElement(children)) {
			return null;
		}

		return cloneElement(children, {
			className: classNames(children.props.className, filterTriggerClassName),
			onClick: composeHandlers(children.props.onClick, handleClick),
			ref,
			...props,
			...aria,
		});
	}

	return (
		<Button
			onClick={handleClick}
			variant={BUTTON_VARIANTS.BORDERLESS}
			className={filterTriggerClassName}
			alignIcon={BUTTON_ICON_ALIGNMENTS.LEFT}
		>
			<FilterIcon />

			<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} isBold>
				<FormattedMessage id="filter.mobile.button.label" />

				{selectedFiltersCount > 0 && ` (${selectedFiltersCount})`}
			</Typography>
		</Button>
	);
});

export const StickyFilterButtonTrigger = ({ className, ...props }) => {
	const { selectedFiltersCount } = useFilterContext();
	const { ref, isSticky } = useOnSticky();

	const buttonWrapperClassName = classNames("sticky-filter-button-trigger__wrapper", {
		"sticky-filter-button-trigger__wrapper--sticky": isSticky,
		[className]: className,
	});

	const buttonClassName = classNames("sticky-filter-button-trigger", {
		"sticky-filter-button-trigger--sticky": isSticky,
		"sticky-filter-button-trigger--has-selected-filters": selectedFiltersCount > 0,
	});

	return (
		<>
			<div ref={ref} />
			<section className={buttonWrapperClassName}>
				<Button
					className={buttonClassName}
					alignIcon={BUTTON_ICON_ALIGNMENTS.LEFT}
					icon={<FilterIcon />}
					{...props}
				>
					<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} isBold>
						<FormattedMessage id="filter.mobile.button.label" />

						{selectedFiltersCount > 0 && ` (${selectedFiltersCount})`}
					</Typography>
				</Button>
			</section>
		</>
	);
};

FilterTrigger.propTypes = {
	asChild: PropTypes.bool,
	className: PropTypes.string,
	children: PropTypes.node,
	onClick: PropTypes.func,
};
